// Status feed client (web) — reads TempDash's service health from the EXTERNALLY
// hosted Better Stack status page, NOT from our own server.
//
// The public tempdash.app/status page is itself served from our infrastructure,
// so it can't be the source of truth during a full outage — but by reading the
// off-infra Better Stack JSON it stays accurate whenever it CAN load, and shows
// e.g. "SMS down" while the rest of the platform is fine. The fully outage-proof
// surface is the Better Stack page itself (linked from the apps' interrupt screens).
//
// Public, no-auth JSON: `<page>/index.json` →
//   data.attributes.aggregate_state  → overall status
//   included[] of type status_page_resource → per-system rows (public_name + status)

// Single source of truth for the status page location. Override at build time
// with VITE_STATUS_PAGE_URL. Defaults to Better Stack's canonical host so the
// feed never depends on the custom-domain DNS; flip to https://status.tempdash.app
// once that CNAME is verified live.
export const STATUS_PAGE_URL =
  import.meta.env.VITE_STATUS_PAGE_URL || 'https://tempdash.betteruptime.com'

const FEED_URL = `${STATUS_PAGE_URL}/index.json`

const TIER = {
  operational:       'operational',
  maintenance:       'maintenance',
  under_maintenance: 'maintenance',
  degraded:          'degraded',
  downtime:          'outage',
  down:              'outage',
}
export function toTier(s) { return TIER[s] || 'operational' }

// Returns a normalised, fail-soft model (same shape the page consumed before).
export async function fetchStatusFeed() {
  try {
    const res = await fetch(FEED_URL, { cache: 'no-store' })
    const json = res.ok ? await res.json().catch(() => null) : null
    const attrs    = json?.data?.attributes
    const included = json?.included
    if (!attrs || !Array.isArray(included)) return unreachable()

    const components = included
      .filter((i) => i.type === 'status_page_resource')
      .sort((a, b) => (a.attributes?.position ?? 0) - (b.attributes?.position ?? 0))
      .map((i) => ({
        id:          i.id,
        name:        i.attributes?.public_name || '',
        description: i.attributes?.explanation || '',
        tier:        toTier(i.attributes?.status),
      }))

    return {
      reachable: true,
      // A feed with NO components is not evidence of health — it means nothing is
      // being monitored, so `aggregate_state` is aggregating an empty set and
      // reports "operational" no matter what is actually burning. Surface that as
      // its own state rather than letting it render as a green all-clear.
      configured: components.length > 0,
      overall: toTier(attrs.aggregate_state),
      updatedAt: new Date(),
      components, incidents: [], maintenances: [],
      url: STATUS_PAGE_URL,
    }
  } catch {
    return unreachable()
  }
}

function unreachable() {
  return {
    reachable: false,
    configured: false,
    overall: 'degraded',
    updatedAt: null,
    components: [],
    incidents: [],
    maintenances: [],
    url: STATUS_PAGE_URL,
  }
}
